import React, { useMemo, useState } from 'react'
import { Container, Tab } from 'react-bootstrap'

import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { SegmentedExternalContactUpdateView } from '../../../../../views/UpdateExternalContact/SegmentedExternalContactUpdateView'
import {
  DefaultExternalContactUpdateView,
  UpdateExternalContactModal,
  UpdateExternalContactModalFooter,
} from '../../../../../views/UpdateExternalContact/UpdateExternalContactModal'
import { INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE, IntegrationServiceId } from '../../../../../common/integrations'
import { User } from '../../../../../models/User'

import { UpdateExternalContactModalInitialization } from '../../../../../views/UpdateExternalContact/UpdateExternalContactModalInitialization'
import { UpdateCRMWorkflow } from '../CRM/workflow/UpdateCRMWorkflow'

import {
  CONTACT_UPDATE_STEP,
  selectContact,
  selectContactUpdateStep,
  setContactUpdateStep,
  setCreateNewCRMContact,
  setExternalId,
} from '../../../../../store/meetingSummary/contactUpdate'
import { useTracking, TrackEvent } from '../../common/tracking'
import { getIntegrationUserTrackingDetails } from '../../tracking'
import { ExternalMeetingContactUpdateProps, useInitializeContactChange } from '../../../../../views/UpdateExternalContact/hooks'
import { UpdateExternalContactSelectStep } from '../../../../../views/UpdateExternalContact/UpdateExternalContactSelectStep'
import { selectSessionMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'

const WORKFLOW_SUPPORTED_CRMS = ['wealthbox', 'redtail']

interface UpdateExternalMeetingContactModalProps {
  user: User
  serviceId: string
  show: boolean
  onCancel: () => void
  onSave: () => void
}

export const UpdateExternalMeetingContactModal: React.FC<UpdateExternalMeetingContactModalProps> = ({ serviceId, user, show, onCancel, onSave }) => {
  const roomSession = useAppSelector(selectRoomSession)
  const sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)

  const dispatch = useAppDispatch()
  const [activeTabKey, setActiveTabKey] = useState<string>('contactFieldsUpdate')
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)

  const { roomId, id: sessionId } = roomSession ?? {}
  const { id: meetingTemplateId } = sessionMeetingTemplate ?? {}

  const integration = user.integrations?.find((e) => e.serviceId === serviceId)
  if (integration) {
    dispatch(setContactUpdateStep(CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP))
    dispatch(setExternalId(integration.externalId))
    dispatch(setCreateNewCRMContact(false))
  }

  const BodyComponent = useMemo(() => {
    if (!roomId || !sessionId || !meetingTemplateId) {
      return null
    }
    return (
      <UpdateExternalMeetingContactModalBody
        serviceId={serviceId}
        user={user}
        roomId={roomId}
        sessionId={sessionId}
        meetingTemplateId={meetingTemplateId}
        activeTabKey={activeTabKey}
        setActiveTabKey={setActiveTabKey}
        onCancel={onCancel}
      />
    )
  }, [serviceId, user, roomId, sessionId, meetingTemplateId, activeTabKey, onCancel])

  const FooterComponent = useMemo(() => {
    if (!roomId || !sessionId || !meetingTemplateId) {
      return null
    }
    return activeTabKey === 'contactFieldsUpdate' ? (
      <UpdateExternalMeetingContactModalFooter
        serviceId={serviceId}
        user={user}
        roomId={roomId}
        sessionId={sessionId}
        meetingTemplateId={meetingTemplateId}
        onSave={onSave}
        onCancel={onCancel}
      />
    ) : null
  }, [activeTabKey, meetingTemplateId, onCancel, onSave, roomId, serviceId, sessionId, user])

  if (!roomSession || !sessionMeetingTemplate) {
    return null
  }

  return (
    <UpdateExternalContactModal
      user={user}
      serviceId={serviceId}
      show={show}
      onCancel={onCancel}
      BodyComponent={BodyComponent}
      FooterComponent={contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP ? FooterComponent : null}
    />
  )
}

interface UpdateExternalMeetingContactModalBodyProps {
  serviceId: string
  user: User
  roomId: string
  sessionId: string
  meetingTemplateId: string
  activeTabKey?: string
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string>>
  onCancel: () => void
}

const UpdateExternalMeetingContactModalBody: React.FC<UpdateExternalMeetingContactModalBodyProps> = ({
  serviceId,
  user,
  roomId,
  sessionId,
  meetingTemplateId,
  activeTabKey,
  setActiveTabKey,
  onCancel,
}) => {
  return (
    <>
      <UpdateExternalContactModalInitialization serviceId={serviceId} />
      <UpdateExternalMeetingContactContent
        serviceId={serviceId}
        user={user}
        roomId={roomId}
        sessionId={sessionId}
        activeTabKey={activeTabKey}
        setActiveTabKey={setActiveTabKey}
        onCancel={onCancel}
        meetingTemplateId={meetingTemplateId}
      />
    </>
  )
}

interface UpdateExternalMeetingContactContentProps {
  serviceId: string
  user: User
  roomId: string
  sessionId: string
  activeTabKey?: string
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string>>
  onCancel: () => void
  meetingTemplateId: string
}

export const UpdateExternalMeetingContactContent: React.FC<UpdateExternalMeetingContactContentProps> = ({
  serviceId,
  user,
  roomId,
  sessionId,
  activeTabKey,
  setActiveTabKey,
  onCancel,
  meetingTemplateId,
}) => {
  const contact = useAppSelector(selectContact)
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)

  const { trackingClient } = useTracking()
  const handleKeySelected = (selectedKey: string, isUnselect: boolean) => {
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_FIELD_SELECTED,
      eventDetails: {
        roomId,
        sessionId,
        selectedKey,
      },
    })

    if (isUnselect) {
      trackingClient.track({
        eventName: TrackEvent.CONTACT_UPDATE_SECTIONS_UNCHECKED,
        eventDetails: {
          serviceId,
          roomId,
          sessionId,
          selectedKey,
        },
      })
    }
  }

  const updateProps: ExternalMeetingContactUpdateProps = useMemo(
    () => ({ type: 'meeting', roomId, sessionId, meetingTemplateId }),
    [meetingTemplateId, roomId, sessionId],
  )

  useInitializeContactChange({
    serviceId,
    user,
    updateProps,
    onCancel,
  })

  const UpdateCRMWorkflowTab = contact ? (
    <Tab eventKey='contactWorkflowUpdate' title='Workflows' className='pb-4'>
      <UpdateCRMWorkflow roomId={roomId} sessionId={sessionId} serviceId={serviceId} contactId={user.id} contact={contact} isSelectedExternalContact={true} />
    </Tab>
  ) : null

  const Tabs = WORKFLOW_SUPPORTED_CRMS.includes(serviceId) ? [UpdateCRMWorkflowTab] : []

  const ExternalContactUpdateView = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId as IntegrationServiceId)
    ? SegmentedExternalContactUpdateView
    : DefaultExternalContactUpdateView

  return (
    <Container className='d-flex flex-column zocks-gap-1.25 p-0'>
      <div>
        {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_SELECT_STEP ? (
          <>
            <UpdateExternalContactSelectStep serviceId={serviceId} onCancel={onCancel} user={user} />
          </>
        ) : null}
        {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP ? (
          <ExternalContactUpdateView
            serviceId={serviceId}
            contactId={user.id}
            activeTabKey={activeTabKey}
            setActiveTabKey={setActiveTabKey}
            Tabs={Tabs}
            onKeySelected={handleKeySelected}
          />
        ) : null}
      </div>
    </Container>
  )
}

interface UpdateExternalMeetingContactModalFooterProps {
  serviceId: string
  user: User
  roomId: string
  sessionId: string
  meetingTemplateId: string
  onCancel: () => void
  onSave: () => void
}

const UpdateExternalMeetingContactModalFooter: React.FC<UpdateExternalMeetingContactModalFooterProps> = ({
  serviceId,
  user,
  roomId,
  sessionId,
  meetingTemplateId,
  onSave,
  onCancel,
}) => {
  const updateProps: ExternalMeetingContactUpdateProps = useMemo(
    () => ({ type: 'meeting', roomId, sessionId, meetingTemplateId }),
    [meetingTemplateId, roomId, sessionId],
  )

  const contact = useAppSelector(selectContact)

  const { trackingClient } = useTracking()

  const handleRerun = () => {
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_RERUN,
      eventDetails: {
        roomId,
        sessionId,
        meetingTemplateId,
        ...getIntegrationUserTrackingDetails(contact),
        userId: user.id,
      },
    })
  }

  const handleSave = () => {
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_SAVED,
      eventDetails: {
        roomId,
        sessionId,
        meetingTemplateId,
        ...getIntegrationUserTrackingDetails(contact),
        userId: user.id,
      },
    })
    onSave()
  }

  return (
    <UpdateExternalContactModalFooter
      serviceId={serviceId}
      user={user}
      updateProps={updateProps}
      onRerun={handleRerun}
      onSave={handleSave}
      onCancel={onCancel}
    />
  )
}
